// NodeMediaHighlight.jsx

import React, { useMemo } from 'react';

// CSS
import './NodeMediaHighlight.css';

// Components
import DocumentView from 'nodetypes/document/view/DocumentView';
import FileView from 'nodetypes/file/view/FileView';
import PhotoView from 'nodetypes/photo/view/PhotoView';
import VideoView from 'nodetypes/video/view/VideoView';

const MEDIA_TYPE_KEYS = new Set(['DOCUMENT', 'FILE', 'PHOTO', 'VIDEO']);

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeNodeMediaTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * @param {unknown} typeKey
 * @returns {boolean}
 */
export function isNodeMediaTypeKey(typeKey) {
  return MEDIA_TYPE_KEYS.has(normalizeNodeMediaTypeKey(typeKey));
}

/**
 * NodeMediaHighlight
 *
 * Full-width media viewer for DOCUMENT / FILE / PHOTO / VIDEO highlight tabs.
 *
 * @param {object|null} node
 * @returns {ReactNode}
 */
const NodeMediaHighlight = React.memo(({ node }) => {
  const typeKey = useMemo(
    () => normalizeNodeMediaTypeKey(node?.type),
    [node?.type]
  );

  if (!node || !isNodeMediaTypeKey(typeKey)) return null;

  return (
    <div className="node-media-highlight-container">
      {typeKey === 'DOCUMENT' ? <DocumentView node={node} /> : null}
      {typeKey === 'FILE' ? <FileView node={node} /> : null}
      {typeKey === 'PHOTO' ? <PhotoView node={node} /> : null}
      {typeKey === 'VIDEO' ? (
        <VideoView
          node={node}
          showControls
        />
      ) : null}
    </div>
  );
});

NodeMediaHighlight.displayName = 'NodeMediaHighlight';

export default NodeMediaHighlight;
